import { createCheckoutSession } from '../api/billing'
import { track } from './analytics/posthog'
import { PLANS, type EpisodeDurationMinutes, type PlanId } from './plans'

interface StartCheckoutOptions {
  fromPlan?: PlanId
  couponCode?: string | null
  trial?: boolean
  trackEvent?: boolean
  /** For weekly plans only: episode duration variant to subscribe to. */
  episodeDurationMinutes?: EpisodeDurationMinutes
}

// Starts a Stripe checkout session for a free -> paid subscription and redirects the browser.
// Throws if called for a non-free to paid transition (those use in-app plan change flow).
export async function startCheckoutSession(plan: PlanId, options: StartCheckoutOptions = {}): Promise<void> {
  const fromPlan = options.fromPlan ?? 'free'

  if (fromPlan === plan) return // nothing to do

  if (fromPlan !== 'free') {
    throw new Error('startCheckoutSession is only for free -> paid transitions; use changePlan flow for others.')
  }

  if (plan === 'free') {
    throw new Error('Cannot checkout to free plan.')
  }

  if (options.trackEvent !== false) {
    track('billing_checkout_started', { plan, fromPlan })
  }

  // Defensive: ensure plan exists
  if (!PLANS[plan]) {
    throw new Error(`Unknown plan id: ${plan}`)
  }

  const { url } = await createCheckoutSession(plan, {
    couponCode: options.couponCode ?? undefined,
    trial: options.trial,
    episodeDurationMinutes: options.episodeDurationMinutes,
  })
  // Redirect to Stripe hosted page
  window.location.href = url
}
